import { api } from '@ottd/api';

/*
 * Fortify two-factor enrolment endpoints (prefix /api). The feature is
 * configured with `confirm: true` (enrolment must be confirmed with a code)
 * and `confirmPassword: true` (enable/disable sit behind `password.confirm`),
 * so the flow is: confirmPassword → enable → (scan) → confirm code.
 */

/** `POST /api/user/confirm-password` - opens the password-confirmation window. */
export function confirmPassword(password: string): Promise<unknown> {
  return api.post<unknown>('/user/confirm-password', { password });
}

/** `POST /api/user/two-factor-authentication` - begin enrolment. */
export function enableTwoFactor(): Promise<unknown> {
  return api.post<unknown>('/user/two-factor-authentication');
}

/** `DELETE /api/user/two-factor-authentication` - disable 2FA. */
export function disableTwoFactor(): Promise<unknown> {
  return api.delete<unknown>('/user/two-factor-authentication');
}

/** `GET /api/user/two-factor-secret-key` - base32 TOTP secret (for the QR). */
export function getTwoFactorSecret(): Promise<{ secretKey: string }> {
  return api.get<{ secretKey: string }>('/user/two-factor-secret-key');
}

/** `GET /api/user/two-factor-qr-code` - Fortify's server-rendered QR SVG. */
export function getTwoFactorQrSvg(): Promise<{ svg: string }> {
  return api.get<{ svg: string }>('/user/two-factor-qr-code');
}

/** `GET /api/user/two-factor-recovery-codes` - one-time recovery codes. */
export function getRecoveryCodes(): Promise<string[]> {
  return api.get<string[]>('/user/two-factor-recovery-codes');
}

/** `POST /api/user/confirmed-two-factor-authentication` - finish enrolment. */
export function confirmTwoFactor(code: string): Promise<unknown> {
  return api.post<unknown>('/user/confirmed-two-factor-authentication', { code });
}

/**
 * Builds an `otpauth://` URI from the Fortify secret so the enrolment QR can be
 * rendered client-side (plan: "client-side QR from the otpauth:// URI").
 */
export function buildOtpAuthUri(secret: string, email: string, issuer = 'OpenTTD.pro'): string {
  const label = encodeURIComponent(`${issuer}:${email}`);
  const params = new URLSearchParams({ secret, issuer });
  return `otpauth://totp/${label}?${params.toString()}`;
}
